/* component-scroll-carousel.js — <scroll-carousel>
   CSS scroll-snap carousel with optional autoplay, prev/next buttons and dots.
   Progressive enhancement: with JS off, the track is a horizontally scrollable,
   scroll-snapping strip (announcement bar messages remain fully readable).

   Attributes:
     autoplay        integer seconds (>=2 enforced) — omit / 0 = no autoplay
     loop            "true" to wrap around at ends (default true)
     aria-label      forwarded to the region

   Desktop mouse users can DRAG the strip (owner request): pointerdown+move
   scrolls the track directly (snap suspended during the drag, restored after);
   a drag past the click threshold suppresses the following click so links and
   buttons inside slides don't fire on release. Touch keeps native scrolling.

   Markup contract (announcement-bar + generic):
     <scroll-carousel autoplay="6">
       <div data-carousel-track>
         <div data-carousel-slide>…</div>
         …
       </div>
       <button data-carousel-prev>…</button>   (optional; created if absent when >1 slide)
       <button data-carousel-next>…</button>
       <div data-carousel-dots></div>           (optional)
     </scroll-carousel>

   Respects prefers-reduced-motion (no autoplay, no smooth scroll).
*/
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ScrollCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;
      this.slides = Array.prototype.slice.call(this.track.querySelectorAll('[data-carousel-slide]'));
      if (this.slides.length <= 1) return; // nothing to rotate

      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      this.dotsWrap = this.querySelector('[data-carousel-dots]');
      this.loop = this.getAttribute('loop') !== 'false';
      // infinite="true": seamless circular scrolling. A full clone set is placed
      // BEFORE and AFTER the real slides; whenever the viewport drifts more than
      // half a set away from the real one, the scroll position is shifted by
      // exactly one set width with snap suspended — pixel-identical content, so
      // the jump is invisible and both swipe directions never hit a dead end.
      this.infinite = this.getAttribute('infinite') === 'true';
      if (this.infinite) this._setupInfinite();

      var sec = parseInt(this.getAttribute('autoplay'), 10);
      this.autoplayMs = (!isNaN(sec) && sec > 0) ? Math.max(sec, 2) * 1000 : 0;

      this.index = 0;
      this._bind();
      this._buildDots();
      this._syncControls();

      // Mandatory scroll-snap can settle on a mid/end snap point during initial
      // layout (fonts/images changing slide widths) — the strip then starts at
      // the END for the first cycle. Normalise to the first slide once painted.
      var self = this;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (!self.track) return;
          // Infinite strips start with the FIRST real slide centred in the
          // viewport (owner 2026-07-29: the desktop trust bar opened with the
          // Trustpilot slide clipped at the left edge) — the prepended clone
          // set fills the space to its left, so nothing reads as cut off.
          if (self.infinite) {
            self.track.scrollLeft = self._centerLeft(0, self._infW());
            // Webfonts/images landing after first paint change slide widths and
            // the mandatory snap re-settles somewhere else — re-centre once the
            // layout is stable (before the first autoplay tick at ≥2s).
            setTimeout(function () {
              if (self.track && self.index === 0 && !self._animating) {
                self._jump(self._centerLeft(0, self._infW()));
              }
            }, 800);
            return;
          }
          if (self.index === 0) self.track.scrollLeft = 0;
        });
      });

      if (this.autoplayMs && !REDUCED) this._startAutoplay();

      // Pause on hover / focus within, resume on leave / blur out.
      if (this.autoplayMs && !REDUCED) {
        this.addEventListener('mouseenter', this._stopAutoplay.bind(this));
        this.addEventListener('mouseleave', this._startAutoplay.bind(this));
        this.addEventListener('focusin', this._stopAutoplay.bind(this));
        this.addEventListener('focusout', this._onFocusOut.bind(this));
        // Pause when the tab is hidden (saves cycles, avoids surprise jumps).
        this._visHandler = function () {
          if (document.hidden) this._stopAutoplay();
          else this._startAutoplay();
        }.bind(this);
        document.addEventListener('visibilitychange', this._visHandler);
      }
    }

    disconnectedCallback() {
      this._stopAutoplay();
      if (this._visHandler) document.removeEventListener('visibilitychange', this._visHandler);
      if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    }

    _bind() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { this._go(this.index - 1); }.bind(this));
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { this._go(this.index + 1); }.bind(this));
      // Track native scrolling (touch / trackpad) to keep index + dots honest.
      // The handler MUST be bound: inside a plain listener `this` is the event
      // target (the track element), where _onScroll does not exist — that threw
      // a TypeError on every scroll and dots never followed native swipes.
      this.track.addEventListener('scroll', function () {
        if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = requestAnimationFrame(this._onScroll.bind(this));
      }.bind(this), { passive: true });

      // ---- Mouse drag-to-scroll (touch already scrolls natively) ----
      var self = this;
      this._dragging = false;
      this.track.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        self._dragging = true;
        self._dragMoved = 0;
        self._dragStartX = e.clientX;
        self._dragStartLeft = self.track.scrollLeft;
        // Mandatory snap fights direct scrollLeft writes — suspend while dragging.
        self._snapBackup = self.track.style.scrollSnapType;
        self.track.style.scrollSnapType = 'none';
        self._stopAutoplay();
      });
      this.track.addEventListener('pointermove', function (e) {
        if (!self._dragging) return;
        var dx = e.clientX - self._dragStartX;
        self._dragMoved = Math.max(self._dragMoved, Math.abs(dx));
        // Capture only once an intentional drag starts, so plain clicks pass through.
        if (self._dragMoved > 5 && !self._captured) {
          self._captured = true;
          try { self.track.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
        }
        if (self._captured) self.track.scrollLeft = self._dragStartLeft - dx;
      });
      var endDrag = function (e) {
        if (!self._dragging) return;
        self._dragging = false;
        if (self._captured) {
          self._captured = false;
          try { self.track.releasePointerCapture(e.pointerId); } catch (err) { /* noop */ }
        }
        self.track.style.scrollSnapType = self._snapBackup || '';
        // Settle on the nearest slide like a native swipe would.
        self._onScroll();
        self._go(self.index);
        if (self.autoplayMs && !REDUCED && !self.matches(':hover')) self._startAutoplay();
        // The suppressing click (if any) fires synchronously after pointerup;
        // clear the drag distance afterwards so a release outside the track
        // can never suppress the NEXT genuine click.
        setTimeout(function () { self._dragMoved = 0; }, 0);
      };
      this.track.addEventListener('pointerup', endDrag);
      this.track.addEventListener('pointercancel', endDrag);
      // A real drag must not activate the link/button under the cursor on release.
      this.track.addEventListener('click', function (e) {
        if (self._dragMoved > 5) { e.preventDefault(); e.stopPropagation(); self._dragMoved = 0; }
      }, true);
    }

    _setupInfinite() {
      var pre = document.createDocumentFragment(), post = document.createDocumentFragment();
      var neuter = function (c) {
        c.removeAttribute('data-carousel-slide');
        c.setAttribute('data-carousel-clone', '');
        c.setAttribute('aria-hidden', 'true');
        var focusables = c.querySelectorAll('a, button, input, select, textarea, [tabindex]');
        for (var j = 0; j < focusables.length; j++) focusables[j].setAttribute('tabindex', '-1');
        return c;
      };
      for (var i = 0; i < this.slides.length; i++) {
        pre.appendChild(neuter(this.slides[i].cloneNode(true)));
        post.appendChild(neuter(this.slides[i].cloneNode(true)));
      }
      this.track.insertBefore(pre, this.slides[0]);
      this.track.appendChild(post);
    }

    // One set width = distance from the prepended clone set to the real set.
    _infW() {
      var first = this.track.querySelector('[data-carousel-clone]');
      return first && this.slides[0] ? this.slides[0].offsetLeft - first.offsetLeft : 0;
    }

    // Scroll position that CENTRES slide i in the viewport (matches the slides'
    // scroll-snap-align: center, so the snap agrees with programmatic targets).
    _centerLeft(i, base) {
      return Math.max(0, base + this._slideLeft(i) - (this.track.clientWidth - this.slides[i].offsetWidth) / 2);
    }

    // Instant, snap-suspended reposition — the invisible wrap jump.
    _jump(left) {
      var snap = this.track.style.scrollSnapType;
      this.track.style.scrollSnapType = 'none';
      this.track.scrollLeft = left;
      this.track.style.scrollSnapType = snap || '';
    }

    /* offsetLeft is relative to the nearest positioned ANCESTOR, which is not
       necessarily the track (e.g. a centred .container adds a ~336px page inset
       at 1920px) — using it raw made every scroll target overshoot and clamp,
       so autoplay bounced between two clamped points instead of cycling.
       Normalise all slide positions against the first slide's offsetLeft, which
       shares the same offset parent, giving true track-scroll coordinates. */
    _slideLeft(i) {
      var base = this.slides[0] ? this.slides[0].offsetLeft : 0;
      return this.slides[i].offsetLeft - base;
    }

    _onScroll() {
      var scroll = this.track.scrollLeft;
      if (this.infinite && !this._animating) {
        var W = this._infW();
        if (W > 0) {
          if (scroll < W * 0.5) { this._jump(scroll + W); scroll += W; }
          else if (scroll > W * 1.5) { this._jump(scroll - W); scroll -= W; }
        }
      }
      var base = this.infinite ? this._infW() : 0;
      var mid = scroll - base + this.track.clientWidth / 2;
      var nearest = 0, best = Infinity;
      for (var i = 0; i < this.slides.length; i++) {
        var center = this._slideLeft(i) + this.slides[i].offsetWidth / 2;
        var d = Math.abs(center - mid);
        if (d < best) { best = d; nearest = i; }
      }
      if (nearest !== this.index) { this.index = nearest; this._syncControls(); }
    }

    _go(i) {
      var n = this.slides.length;
      if (this.infinite) {
        // Never wrap the TARGET backwards: past either end we scroll smoothly into
        // the adjacent clone set (same direction), and the post-animation
        // correction in _onScroll silently re-centres onto the real set.
        var W = this._infW();
        var wrapped = ((i % n) + n) % n;
        var offset = i >= n ? W : (i < 0 ? -W : 0);
        this.index = wrapped;
        this._animating = true;
        var self = this;
        clearTimeout(this._animT);
        this._animT = setTimeout(function () { self._animating = false; self._onScroll(); }, 700);
        this.track.scrollTo({ left: this._centerLeft(wrapped, W) + offset, behavior: REDUCED ? 'auto' : 'smooth' });
        this._syncControls();
        return;
      }
      if (i < 0) i = this.loop ? n - 1 : 0;
      else if (i >= n) i = this.loop ? 0 : n - 1;
      this.index = i;
      this.track.scrollTo({ left: this._slideLeft(i), behavior: REDUCED ? 'auto' : 'smooth' });
      this._syncControls();
    }

    _buildDots() {
      if (!this.dotsWrap) return;
      if (this.infinite) { this.dotsWrap.innerHTML = ''; return; }
      this.dotsWrap.innerHTML = '';
      this.dots = [];
      for (var i = 0; i < this.slides.length; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'carousel__dot';
        b.setAttribute('aria-label', String(i + 1));
        (function (idx, self) { b.addEventListener('click', function () { self._go(idx); }); })(i, this);
        this.dotsWrap.appendChild(b);
        this.dots.push(b);
      }
    }

    _syncControls() {
      if (this.dots) {
        for (var i = 0; i < this.dots.length; i++) {
          var on = i === this.index;
          this.dots[i].setAttribute('aria-current', on ? 'true' : 'false');
          this.dots[i].classList.toggle('is-active', on);
        }
      }
      if (!this.loop) {
        if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
        if (this.nextBtn) this.nextBtn.disabled = this.index === this.slides.length - 1;
      }
    }

    _startAutoplay() {
      this._stopAutoplay();
      if (!this.autoplayMs) return;
      // autoplay-mobile-only: rotate only on phone widths — desktop variants of these
      // strips show all items statically (rotating a non-overflowing or partially
      // overflowing desktop row clipped items at the edges).
      if (this.hasAttribute('autoplay-mobile-only') && window.matchMedia('(min-width: 769px)').matches) return;
      this._timer = setInterval(function () { this._go(this.index + 1); }.bind(this), this.autoplayMs);
    }

    _stopAutoplay() {
      if (this._timer) { clearInterval(this._timer); this._timer = null; }
    }

    _onFocusOut(e) {
      // Only resume if focus actually left the component.
      if (!this.contains(e.relatedTarget)) this._startAutoplay();
    }
  }

  if (!customElements.get('scroll-carousel')) {
    customElements.define('scroll-carousel', ScrollCarousel);
  }
})();
